import isPlainObject from 'lodash/isPlainObject'
import get from 'lodash/get'
import has from 'lodash/has'
import isEmpty from 'lodash/isEmpty'
import Cookies from 'js-cookie'

import { getBrowser, getOs, uuidv4 } from './utils'
import MarxyApiService from './api'

import {
  MarxyConfig,
  MarxyEvents,
  Marxy,
  MarxyProperties,
} from '~/utils/marxy/types'

const MARXY_VERSION = '0.1.0'
const MARXY_COOKIE_NAME = '__exponea_etc__'
const TIME_SECOND = 1000

export const marxy: Marxy = {
  config: {},
  cookieName: MARXY_COOKIE_NAME,

  generateCookie() {
    const cookieDomain = `.${
      (window.location.hostname.match(/((mall|mimovrste)[.\-\w\d]+)/) || [])[0]
    }`

    Cookies.set(this.cookieName, uuidv4(), { domain: cookieDomain })
  },

  getParamsFromUrl(params: Array<string>) {
    const query = location.search ? location.search.substr(1) : ''
    const result: any = {}
    query.split('&').forEach((part) => {
      const [key, value] = part.split('=')
      if (params.includes(key)) {
        result[key] = decodeURIComponent(value)
      }
    })

    return result
  },

  init(config: MarxyConfig, cookieName = MARXY_COOKIE_NAME) {
    this.config = config
    this.cookieName = cookieName || 'marxy'
    if (!Cookies.get(cookieName)) {
      this.generateCookie()
    }
  },

  async trackEvent(
    type: MarxyEvents,
    properties?: MarxyProperties,
    customCustomerId?: string
  ) {
    if (!window.marxy) {
      return
    }

    if (!type) {
      console.error('Marxy log: argument type is required!')
      return
    }

    const data: any = {
      customer_ids: {
        cookie: Cookies.get(this.cookieName) || null,
      },
      event_type: type || undefined,
      properties: (isPlainObject(properties) && properties) || {},
      timestamp: Math.round(new Date().getTime() / TIME_SECOND),
    }

    const exponeaId = get(this.config, 'marxy.exponeaId', null)
    if (exponeaId) {
      data.customer_ids.exponea_id = exponeaId
    } else {
      const customerId =
        customCustomerId || get(this.config, 'customer.id', null)
      if (customerId) data.customer_ids.customer_id = customerId
    }

    data.properties.personalization = get(
      this.config,
      'consents.gdprPersonalization'
    )
      ? 'accept'
      : 'reject'
    data.properties.account_type = has(data, 'customer_ids.customer_id')
      ? 'registered'
      : 'guest'
    data.properties.location = window.location.href || null
    data.properties.path = window.location.pathname || null
    data.properties.referrer = document.referrer || null
    data.properties.os = getOs() || null
    data.properties.device = navigator.userAgent || null
    data.properties.browser = getBrowser() || null

    if (data.properties.location) {
      data.properties = {
        ...data.properties,
        ...this.getParamsFromUrl(['utm_campaign', 'utm_medium', 'utm_source']),
      }
    }

    try {
      if (data.customer_ids.cookie && has(this.config, 'marxy.apiUrl')) {
        if (Cookies.get('_mgMarxyDebugMode') === '3') {
          await MarxyApiService.trackPost(data)

          return
        }
        await MarxyApiService.trackGet(data)
      } else {
        throw new Error('Marxy track: Missing required parameters')
      }
    } catch (e) {
      if (!e.params && e.request && e.response) {
        e.params = {
          trackEventData: data,
          xhrData: {
            code: e.code,
            method: e.config && e.config.method,
            response: e.response && e.response.data,
            responseText: e.request && e.request.responseText,
            responseURL: e.request && e.request.responseURL,
            status: e.request && e.request.status,
          },
        }
      }
      console.log(e)
      await MarxyApiService.apiLog(e.message, e)
    }
  },

  version: MARXY_VERSION,
}

export const initMarxy = (config: MarxyConfig) => {
  const { apiUrl, marxySpaEnabled } = config.marxy || {}
  if (!apiUrl || !marxySpaEnabled || isEmpty(config)) {
    return
  }

  if (!window.marxy) {
    Object.defineProperty(window, 'marxy', {
      value: marxy,
      writable: false,
    })
  }

  marxy.init(config)
}

export default initMarxy
